import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Key,
  Plus,
  MoreVertical,
  Copy,
  Ban,
  Trash2,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { getApiKeys, revokeApiKey, deleteApiKey } from "@/lib/apiKeysService";
import { CreateApiKeyDialog } from "./CreateApiKeyDialog";
import { ApiKeyCreatedDialog } from "./ApiKeyCreatedDialog";
import { RevokeApiKeyDialog } from "./RevokeApiKeyDialog";

const PERMISSION_LABELS = {
  spaces: "Spaces",
  members: "Members",
  requirements: "Requirements",
  assignments: "Assignments",
  compliance: "Compliance",
  matrix: "Matrix",
  qualifications: "Qualifications",
  checkins: "Check-ins",
  leave_requests: "Leave Requests",
  files: "Files",
  sms: "SMS",
};

function getKeyStatus(key) {
  if (key.revoked_at || !key.is_active) return "revoked";
  if (key.expires_at && new Date(key.expires_at) < new Date()) return "expired";
  return "active";
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return "Never";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function PermissionBadges({ permissions }) {
  if (!permissions || Object.keys(permissions).length === 0) {
    return <span className="text-muted-foreground text-xs">None</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {Object.entries(permissions).map(([resource, actions]) => {
        const label = PERMISSION_LABELS[resource] || resource;
        const hasRead = actions.includes("read");
        const hasWrite = actions.includes("write");
        const suffix = hasRead && hasWrite ? "R/W" : hasRead ? "R" : "W";
        return (
          <Badge key={resource} variant="secondary" className="text-[10px] px-1.5 py-0">
            {label}: {suffix}
          </Badge>
        );
      })}
    </div>
  );
}

export function ApiKeysSection({ company }) {
  const { userProfile } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createdKeyData, setCreatedKeyData] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchKeys = async () => {
    if (!company?.id) return;
    try {
      setLoading(true);
      const data = await getApiKeys(company.id);
      setApiKeys(data || []);
    } catch (err) {
      console.error("Failed to fetch API keys:", err);
      toast.error("Failed to load API keys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
  }, [company?.id]);

  const handleKeyCreated = (keyData) => {
    setShowCreateDialog(false);
    setCreatedKeyData(keyData);
    fetchKeys();
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      setActionLoading(revokeTarget.id);
      await revokeApiKey(revokeTarget.id, userProfile?.id);
      toast.success(`API key "${revokeTarget.name}" has been revoked`);
      setRevokeTarget(null);
      fetchKeys();
    } catch (err) {
      console.error("Failed to revoke key:", err);
      toast.error("Failed to revoke API key");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setActionLoading(deleteTarget.id);
      await deleteApiKey(deleteTarget.id);
      toast.success(`API key "${deleteTarget.name}" has been deleted`);
      setDeleteTarget(null);
      fetchKeys();
    } catch (err) {
      console.error("Failed to delete key:", err);
      toast.error("Failed to delete API key");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-100 rounded-lg">
                <Key className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <CardTitle className="text-lg">API Keys</CardTitle>
                <CardDescription>
                  Manage API keys for external integrations and programmatic access
                </CardDescription>
              </div>
            </div>
            <Button size="sm" onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Create Key
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Key className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                No API keys yet. Create one to enable external integrations.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map((key) => {
                const status = getKeyStatus(key);
                return (
                  <div
                    key={key.id}
                    className="flex items-center justify-between p-3 border rounded-lg bg-background"
                  >
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">{key.name}</span>
                        <Badge
                          variant={
                            status === "active"
                              ? "default"
                              : status === "revoked"
                              ? "destructive"
                              : "secondary"
                          }
                          className="text-[10px] px-1.5 py-0"
                        >
                          {status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <code className="bg-muted px-1.5 py-0.5 rounded text-[11px]">
                          {key.key_prefix}...
                        </code>
                        <span>Created {new Date(key.created_date).toLocaleDateString()}</span>
                        <span>Last used: {formatRelativeTime(key.last_used_at)}</span>
                        {key.expires_at && (
                          <span>
                            Expires: {new Date(key.expires_at).toLocaleDateString()}
                          </span>
                        )}
                        <span>{key.request_count?.toLocaleString()} requests</span>
                      </div>
                      <PermissionBadges permissions={key.permissions} />
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 ml-2">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            navigator.clipboard.writeText(key.key_prefix + "...");
                            toast.success("Key prefix copied");
                          }}
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Copy Prefix
                        </DropdownMenuItem>
                        {status === "active" && (
                          <DropdownMenuItem
                            onClick={() => setRevokeTarget(key)}
                            className="text-orange-600"
                          >
                            <Ban className="h-4 w-4 mr-2" />
                            Revoke
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => setDeleteTarget(key)}
                          className="text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create API Key Dialog */}
      <CreateApiKeyDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        companyId={company?.id}
        onCreated={handleKeyCreated}
      />

      {/* Key Created Dialog (shows key once) */}
      <ApiKeyCreatedDialog
        open={!!createdKeyData}
        onOpenChange={(open) => !open && setCreatedKeyData(null)}
        keyData={createdKeyData}
      />

      {/* Revoke Confirmation Dialog */}
      <RevokeApiKeyDialog
        open={!!revokeTarget}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
        keyName={revokeTarget?.name}
        onConfirm={handleRevoke}
        loading={actionLoading === revokeTarget?.id}
        action="revoke"
      />

      {/* Delete Confirmation Dialog */}
      <RevokeApiKeyDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        keyName={deleteTarget?.name}
        onConfirm={handleDelete}
        loading={actionLoading === deleteTarget?.id}
        action="delete"
      />
    </>
  );
}
